import { Controller } from "@hotwired/stimulus"

let googleMapsLoaderPromise = null
let authFailureHookInstalled = false
// Survives Turbo Drive visits along with `window.google`. Google fires gm_authFailure
// only once per page load, so controllers connecting later must read this instead.
let mapsFailureReason = null

// Dispatched on document so every mounted instance falls back, including ones that
// already initialized the autocomplete.
const MANUAL_FALLBACK_EVENT = "place-search:manual-fallback"
const LOAD_TIMEOUT_MS = 10000

const FAILURE_HINTS = {
  missing_key:
    "No API key configured (credentials: google_maps.api_key).",
  auth_failure:
    "Google rejected the API key for this site (gm_authFailure: usually " +
    "RefererNotAllowedMapError or InvalidKeyMapError). Authorize this referrer in " +
    "Google Cloud Console → Credentials → API key → Website restrictions.",
  load_error:
    "Could not download maps.googleapis.com (network down, DNS, or an ad blocker).",
  timeout:
    `The Google Maps script did not finish loading within ${LOAD_TIMEOUT_MS / 1000}s.`,
  places_missing:
    "The script loaded but the 'places' library is not available."
}

function reportMapsFailure(reason, detail) {
  mapsFailureReason = reason

  const referrer = `${window.location.origin}/*`
  console.error(
    [
      `[place-search] Google Maps is unavailable — switched to manual address fields.`,
      `Reason: ${reason}. ${FAILURE_HINTS[reason] || ""}`,
      `Referrer to authorize: ${referrer}`,
      detail ? `Detail: ${detail}` : null
    ]
      .filter(Boolean)
      .join("\n")
  )

  document.dispatchEvent(
    new CustomEvent(MANUAL_FALLBACK_EVENT, { detail: { reason } })
  )
}

// Google calls window.gm_authFailure when it rejects the key. In that case the script
// still loads and the callback still runs, so this hook is the only failure signal.
function installAuthFailureHook() {
  if (authFailureHookInstalled) return
  authFailureHookInstalled = true

  const previous = window.gm_authFailure
  window.gm_authFailure = () => {
    googleMapsLoaderPromise = null
    reportMapsFailure("auth_failure")
    if (typeof previous === "function") previous()
  }
}

function loadGoogleMaps(apiKey) {
  if (window.google?.maps?.places) {
    return Promise.resolve()
  }

  if (googleMapsLoaderPromise) {
    return googleMapsLoaderPromise
  }

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const callbackName = "__waveGoogleMapsPlacesReady"
    const timeoutId = setTimeout(() => {
      if (window.google?.maps?.places) return
      googleMapsLoaderPromise = null
      reject(new Error("timeout"))
    }, LOAD_TIMEOUT_MS)

    window[callbackName] = () => {
      clearTimeout(timeoutId)
      delete window[callbackName]
      resolve()
    }

    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&language=es&region=CL&callback=${callbackName}&loading=async`
    script.async = true
    script.defer = true
    script.onerror = () => {
      clearTimeout(timeoutId)
      googleMapsLoaderPromise = null
      reject(new Error("script load error"))
    }
    document.head.appendChild(script)
  })

  return googleMapsLoaderPromise
}

function addressComponent(components, type) {
  const match = (components || []).find((component) => component.types.includes(type))
  return match?.long_name || ""
}

function normalizeRegionKey(value) {
  return value
    .toString()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/^region( de)?\s+/, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

const GOOGLE_REGION_ALIASES = {
  metropolitana: "Metropolitana de Santiago",
  "metropolitana de santiago": "Metropolitana de Santiago",
  "santiago metropolitan": "Metropolitana de Santiago",
  "santiago metropolitan region": "Metropolitana de Santiago",
  ohiggins: "Libertador General Bernardo O'Higgins",
  "o higgins": "Libertador General Bernardo O'Higgins",
  "libertador general bernardo ohiggins": "Libertador General Bernardo O'Higgins",
  aysen: "Aysén del General Carlos Ibáñez del Campo",
  "aysen del general carlos ibanez del campo": "Aysén del General Carlos Ibáñez del Campo",
  magallanes: "Magallanes y de la Antártica Chilena",
  "magallanes y de la antartica chilena": "Magallanes y de la Antártica Chilena",
  araucania: "La Araucanía",
  "la araucania": "La Araucanía",
  nuble: "Ñuble",
  biobio: "Biobío",
  "bio bio": "Biobío",
  tarapaca: "Tarapacá",
  valparaiso: "Valparaíso",
  "los rios": "Los Ríos"
}

export default class extends Controller {
  static targets = [
    "searchInput",
    "searchPanel",
    "selectedPanel",
    "manualPanel",
    "detailsPanel",
    "fallbackNotice",
    "manualRequired",
    "selectedName",
    "selectedAddress",
    "clearButton",
    "placeName",
    "googlePlaceId",
    "latitude",
    "longitude",
    "addressLine1",
    "addressLine2",
    "city",
    "region",
    "country",
    "formattedAddress"
  ]

  static values = {
    apiKey: String,
    selected: Boolean,
    required: Boolean,
    regions: Array
  }

  connect() {
    this.manualMode = false
    this.autocomplete = null

    this.onManualFallback = () => this.enterManualMode()
    document.addEventListener(MANUAL_FALLBACK_EVENT, this.onManualFallback)

    // checkout-fulfillment-method hides/shows the delivery panel; re-sync `required` so
    // no required field is left inside a hidden panel.
    this.onFulfillmentChange = () => this.syncRequired()
    document.addEventListener("checkout-fulfillment-method:changed", this.onFulfillmentChange)

    if (!this.apiKeyValue) {
      reportMapsFailure("missing_key")
      return
    }

    if (mapsFailureReason) {
      this.enterManualMode()
      return
    }

    this.syncRequired()
    installAuthFailureHook()
    loadGoogleMaps(this.apiKeyValue)
      .then(() => this.initAutocomplete())
      .catch((error) => {
        const reason = error?.message === "timeout" ? "timeout" : "load_error"
        reportMapsFailure(reason, error?.message)
      })
  }

  disconnect() {
    document.removeEventListener(MANUAL_FALLBACK_EVENT, this.onManualFallback)
    document.removeEventListener("checkout-fulfillment-method:changed", this.onFulfillmentChange)

    if (this.autocomplete && window.google?.maps?.event) {
      google.maps.event.clearInstanceListeners(this.autocomplete)
    }
    this.autocomplete = null
  }

  initAutocomplete() {
    if (!this.hasSearchInputTarget) return
    if (!window.google?.maps?.places) {
      reportMapsFailure("places_missing")
      return
    }

    this.autocomplete = new google.maps.places.Autocomplete(this.searchInputTarget, {
      fields: ["place_id", "name", "formatted_address", "geometry", "address_components"],
      componentRestrictions: { country: "cl" }
    })

    this.autocomplete.addListener("place_changed", () => this.onPlaceChanged())
  }

  // Shows the classic fields (the same inputs the autocomplete fills) and hides the
  // search box. Values already entered are kept because they are the same inputs.
  enterManualMode() {
    if (this.manualMode) return
    this.manualMode = true

    if (this.hasSearchPanelTarget) this.searchPanelTarget.classList.add("hidden")
    if (this.hasSelectedPanelTarget) this.selectedPanelTarget.classList.add("hidden")
    if (this.hasManualPanelTarget) this.manualPanelTarget.classList.remove("hidden")
    if (this.hasDetailsPanelTarget) this.detailsPanelTarget.classList.remove("hidden")
    if (this.hasFallbackNoticeTarget) this.fallbackNoticeTarget.classList.remove("hidden")

    this.syncRequired()
  }

  onPlaceChanged() {
    const place = this.autocomplete?.getPlace()
    if (!place?.place_id) return

    const components = place.address_components || []
    const streetNumber = addressComponent(components, "street_number")
    const route = addressComponent(components, "route")
    const placeName = place.name || ""
    const streetLine = [route, streetNumber].filter(Boolean).join(" ")
    const addressLine1 = streetLine || placeName
    const city =
      addressComponent(components, "locality") ||
      addressComponent(components, "administrative_area_level_3") ||
      addressComponent(components, "sublocality") ||
      addressComponent(components, "administrative_area_level_2") ||
      placeName
    const region = this.coerceRegion(
      addressComponent(components, "administrative_area_level_1") || city
    )
    const countryComponent = addressComponent(components, "country")
    const country = countryComponent === "Chile" || countryComponent === "CL" ? "Chile" : (countryComponent || "Chile")
    const formattedAddress = place.formatted_address || [placeName, addressLine1, city, region, country].filter(Boolean).join(", ")
    const lat = place.geometry?.location?.lat?.()
    const lng = place.geometry?.location?.lng?.()

    this.placeNameTarget.value = placeName
    this.googlePlaceIdTarget.value = place.place_id
    this.latitudeTarget.value = lat != null ? String(lat) : ""
    this.longitudeTarget.value = lng != null ? String(lng) : ""
    this.addressLine1Target.value = addressLine1
    if (this.hasAddressLine2Target) this.addressLine2Target.value = ""
    this.cityTarget.value = city
    this.setRegionValue(region)
    this.countryTarget.value = country
    this.formattedAddressTarget.value = formattedAddress

    this.showSelected(placeName || formattedAddress, placeName ? formattedAddress : "")
    this.searchInputTarget.value = ""
    this.syncRequired()
  }

  clear(event) {
    event?.preventDefault()

    this.placeNameTarget.value = ""
    this.googlePlaceIdTarget.value = ""
    this.latitudeTarget.value = ""
    this.longitudeTarget.value = ""
    this.addressLine1Target.value = ""
    if (this.hasAddressLine2Target) this.addressLine2Target.value = ""
    this.cityTarget.value = ""
    this.setRegionValue("")
    this.countryTarget.value = "Chile"
    this.formattedAddressTarget.value = ""

    if (this.hasSelectedNameTarget) {
      this.selectedNameTarget.querySelector("span")?.replaceChildren()
    }
    if (this.hasSelectedAddressTarget) {
      this.selectedAddressTarget.textContent = ""
      this.selectedAddressTarget.classList.add("hidden")
    }

    if (this.hasSelectedPanelTarget) {
      this.selectedPanelTarget.classList.add("hidden")
    }
    if (this.hasDetailsPanelTarget && !this.manualMode) {
      this.detailsPanelTarget.classList.add("hidden")
    }
    if (this.hasSearchPanelTarget && !this.manualMode) {
      this.searchPanelTarget.classList.remove("hidden")
    }
    this.selectedValue = false
    this.syncRequired()

    if (this.hasSearchInputTarget && !this.manualMode) {
      this.searchInputTarget.focus()
    }
  }

  showSelected(name, address) {
    if (this.hasSelectedNameTarget) {
      const span = this.selectedNameTarget.querySelector("span")
      if (span) span.textContent = name
    }

    if (this.hasSelectedAddressTarget) {
      if (address) {
        this.selectedAddressTarget.textContent = address
        this.selectedAddressTarget.classList.remove("hidden")
      } else {
        this.selectedAddressTarget.textContent = ""
        this.selectedAddressTarget.classList.add("hidden")
      }
    }

    if (this.hasSearchPanelTarget) {
      this.searchPanelTarget.classList.add("hidden")
    }
    if (this.hasSelectedPanelTarget) {
      this.selectedPanelTarget.classList.remove("hidden")
    }
    if (this.hasDetailsPanelTarget) {
      this.detailsPanelTarget.classList.remove("hidden")
    }
    this.selectedValue = true
    this.syncRequired()
  }

  setRegionValue(region) {
    if (!this.hasRegionTarget) return

    // The region field is a <select>: a value outside the list would be dropped
    // silently, so add it as an option before assigning it.
    if (region && this.regionTarget.tagName === "SELECT") {
      const options = Array.from(this.regionTarget.options)
      if (!options.some((option) => option.value === region)) {
        this.regionTarget.add(new Option(region, region))
      }
    }

    this.regionTarget.value = region
    this.regionTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  coerceRegion(raw) {
    if (!raw) return ""
    if (this.regionsValue?.includes(raw)) return raw

    const key = normalizeRegionKey(raw)
    if (GOOGLE_REGION_ALIASES[key]) return GOOGLE_REGION_ALIASES[key]

    const match = (this.regionsValue || []).find((region) => normalizeRegionKey(region) === key)
    return match || raw
  }

  // A `required` input inside a hidden panel blocks submit without showing anything
  // ("not focusable"), so never mark fields that are not visible.
  syncRequired() {
    const visible = !this.element.closest(".hidden")

    if (this.hasSearchInputTarget) {
      this.toggleRequired(
        this.searchInputTarget,
        visible && this.requiredValue && !this.manualMode && !this.selectedValue
      )
    }

    const manualRequired = visible && this.requiredValue && this.manualMode
    this.manualRequiredTargets.forEach((field) => this.toggleRequired(field, manualRequired))
  }

  toggleRequired(field, required) {
    if (required) {
      field.setAttribute("required", "required")
    } else {
      field.removeAttribute("required")
    }
  }
}
